import { formatDate } from '@/modules/_helpers/formatDate'
import { fileOriginToDisplayName } from '@/modules/file/utils'
import Decimal from 'decimal.js'
import { isMeinBuero } from '@/utils/build-env'

import lang from '@/lang'

export const selectColumn = {
  label: 'select',
  name: 'select',
  suppressAutoSize: true,
  resizable: false,
  suppressMovable: true,
  filter: false,
  width: 60,
  minWidth: 60,
  maxWidth: 60,
  suppressMenu: true,
  lockPosition: true,
  cellRenderer: 'selectCell',
  headerComponent: 'selectCellHeader',
  default: true,
  active: true,
}

// FLAG:useDocuAi — descriptor for the list-view analysis-state column ("Prüfung"). Additive and
// inert: only the gated injector in document-list.js ever puts it into the list's columns, and it is
// never persisted to the column settings, so flag-OFF never sees it. Its ag-Grid props come from the
// `invoiceAnalysisState` case in getColumnProps below.
export const invoiceAnalysisColumn = {
  name: 'invoiceAnalysisState',
  label: lang.documents.invoiceAnalysisColumn,
  required: false,
  active: true,
}

export const getColumnProps = (name) => {
  const column = {
    displayed: true,
    cellRenderer: 'menuCell',
    minWidth: 100,
    maxWidth: 100,
    default: false,
  }

  switch (name) {
    case 'isFavorite':
      return {
        ...column,
        orderId: 0,
        maxWidth: 24,
        cellRenderer: 'favoriteCell',
        headerComponent: 'favoriteCellHeader',
      }
    case 'unseen':
      return {
        ...column,
        orderId: 1,
        maxWidth: 24,
        cellRenderer: 'unseenDocumentCell',
        headerComponent: 'unseenDocumentHeader',
      }
    case 'recordDate':
      return {
        ...column,
        orderId: 2,
        valueFormatter: ({ value }) => {
          return (value && formatDate(value)) || '–'
        },
        default: true,
        minWidth: 125,
        maxWidth: 125,
      }
    case 'addedAt':
      return {
        ...column,
        orderId: 3,
        valueFormatter: ({ value }) => {
          return (value && formatDate(value)) || '–'
        },
        minWidth: 150,
        maxWidth: 150,
      }
    case 'mimetype':
      return {
        ...column,
        orderId: 5,
        cellRenderer: 'fileIconCell',
        headerComponent: 'fileIconHeader',
      }
    case 'name':
      return {
        ...column,
        default: true,
        orderId: 4,
        minWidth: 300,
        maxWidth: 500,
        valueFormatter: function({ value }) {
          return value ?? 'wird geladen...'
        },
        cellRenderer: 'previewCell',
      }
    case 'size':
      return {
        ...column,
        orderId: 6,
        default: true,
        valueFormatter: ({ value }) => {
          if (!value) return ''

          const size = value / 1024
          if (size < 1024) return `${new Decimal(size).toDP(0).toNumber()} KB`
          if (size < 1024 * 1024) return `${new Decimal(size / 1024).toDP(1).toNumber()} MB`
          return `${new Decimal(size / (1024 * 1024)).toDP(1).toNumber()} GB`
        },
      }
    case 'kind':
      return {
        ...column,
        default: true,
        orderId: 7,
        valueFormatter: ({ node: { data } }) => {
          if (!data) return ''
          const { tags } = data
          if (!tags) return ''
          const kindTag = tags.find((tag) => tag?.isSystem === true)
          return kindTag?.label ?? ''
        },
        minWidth: 145,
        maxWidth: 145,
      }
    // FLAG:useDocuAi — list-view analysis-state cell. Float orderId 7.5 slots it between `kind` (7)
    // and `tags` (8) WITHOUT renumbering any existing column, so flag-OFF column order is unchanged.
    case 'invoiceAnalysisState':
      return {
        ...column,
        orderId: 7.5,
        cellRenderer: 'invoiceAnalysisStateCell',
        filter: false,
        sortable: false,
        minWidth: 145,
        maxWidth: 160,
      }
    case 'tags':
      return {
        ...column,
        orderId: 8,
        cellRenderer: 'tagsCell',
      }
    case 'isAssigned':
      return {
        ...column,
        orderId: 9,
        minWidth: 120,
        maxWidth: 120,
        valueFormatter: function({ value, node: { data } }) {
          return value || data?.externalOwnerSystem !== null ? lang.generics.yes : lang.generics.no
        },
      }
    case 'updatedAt':
      return {
        ...column,
        orderId: 10,
        valueFormatter: ({ value }) => {
          return value && formatDate(value)
        },
        minWidth: 135,
        maxWidth: 135,
      }
    case 'origin':
      return {
        ...column,
        orderId: 11,
        valueFormatter: ({ value }) => {
          return fileOriginToDisplayName(value)
        },
      }
    case 'createdBy':
      return {
        ...column,
        orderId: 12,
        minWidth: 180,
        maxWidth: 180,
        valueFormatter: ({ value }) => {
          return (value?.firstName ? (value.firstName + (value?.lastName ? ' ' + value.lastName : '')) : value?.email) ?? ''
        },
      }
    case 'metaData.datev.uploadDate':
      return {
        ...column,
        default: true,
        orderId: 13,
        valueFormatter: ({ data }) => {
          if (!data?.metaData?.datev?.uploadDate) return ''
          return formatDate(data.metaData.datev.uploadDate)
        },
        valueGetter: 'data.metaData.datev.uploadDate',
        minWidth: 195,
        maxWidth: 195,
      }
  }
}
